/**
 * Utility functions for widgets
 */

// Parse string attributes to appropriate types
export function parseWidgetAttribute(
  value: string | null,
  defaultValue: unknown,
): unknown {
  if (value === null || value === undefined) {
    return defaultValue
  }

  // Try to parse as JSON first (for objects, arrays, booleans, numbers)
  try {
    return JSON.parse(value)
  } catch {
    // If JSON parsing fails, return as string
    return value
  }
}

// Convert props object to HTML attributes for web component
export function propsToAttributes(
  props: Record<string, unknown>,
): Record<string, string> {
  const attributes: Record<string, string> = {}

  Object.entries(props).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      // Convert camelCase to kebab-case for HTML attributes
      const attributeName = key
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .toLowerCase()
      attributes[attributeName] =
        typeof value === 'string' ? value : JSON.stringify(value)
    }
  })

  return attributes
}

// Convert HTML attributes back to props object
export function attributesToProps(element: Element): Record<string, unknown> {
  const props: Record<string, unknown> = {}

  Array.from(element.attributes).forEach((attr) => {
    // Convert kebab-case to camelCase
    const propName = attr.name.replace(/-([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    )
    props[propName] = parseWidgetAttribute(attr.value, attr.value)
  })

  return props
}

// Debug helper for widget development
export function debugWidget(widgetName: string, props: unknown) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[${widgetName}] Props:`, props)
  }
}

/** Hosts of the marketing site, where widgets run for real visitors. */
const PRODUCTION_HOSTS = ['muir.ai', 'www.muir.ai']

/**
 * The Statsig environment tier for a widget running on `hostname`.
 *
 * Anywhere but the marketing site (localhost, the widget dev page, preview
 * deployments) is development, so testing a widget does not count towards
 * production experiment results.
 */
export function statsigTier(hostname: string): 'production' | 'development' {
  return PRODUCTION_HOSTS.includes(hostname) ? 'production' : 'development'
}
